import React, { useState } from 'react';
import { Smartphone, Tablet, Monitor, Link2, Unlink2 } from 'lucide-react';

/**
 * Available CSS units for compact inline display
 */
const COMPACT_UNITS = ['px', 'rem', '%'];

/**
 * Full units array for expanded view
 */
const UNITS = [
  { value: 'px', label: 'px' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' },
  { value: 'vh', label: 'vh' },
  { value: 'vw', label: 'vw' },
  { value: '%', label: '%' },
];

/**
 * Breakpoint definitions
 */
const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', short: 'M', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', short: 'T', icon: Tablet },
  { id: 'desktop', label: 'Desktop', short: 'D', icon: Monitor },
];

/**
 * Default spacing object
 */
const DEFAULT_SPACING = { top: 0, right: 0, bottom: 0, left: 0, unit: 'px' };

/**
 * ResponsiveSpacingField - 4-value spacing input with breakpoint support
 *
 * Combines the breakpoint functionality from ResponsiveDimensionField with
 * the spacing inputs from SpacingField.
 *
 * Data Structure:
 * {
 *   base: { top: 0, right: 0, bottom: 0, left: 0, unit: 'px' },
 *   tablet: null | { top, right, bottom, left, unit },
 *   desktop: null | { top, right, bottom, left, unit }
 * }
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} props.field.type - 'responsivePadding' or 'responsiveMargin'
 * @param {Object} [props.field.default] - Default responsive spacing
 * @param {Object} props.value - Current value { base, tablet?, desktop? }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 * @param {string} [props.activeBreakpoint] - External breakpoint control
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes
 */
export default function ResponsiveSpacingField({
  field,
  value,
  onChange,
  disabled = false,
  compact = false,
  activeBreakpoint: externalBreakpoint,
  onBreakpointChange,
}) {
  const [isLinked, setIsLinked] = useState(false);
  const [localBreakpoint, setLocalBreakpoint] = useState('base');

  // Use external breakpoint if provided, otherwise use local state
  const activeBreakpoint = externalBreakpoint ?? localBreakpoint;

  // Determine if this is a margin field (supports 'auto' for left/right)
  const isMargin = field.type === 'responsiveMargin';

  const handleBreakpointChange = (bp) => {
    if (onBreakpointChange) {
      onBreakpointChange(bp);
    } else {
      setLocalBreakpoint(bp);
    }
  };

  /**
   * Normalize value to responsive format
   * Handles both old format { top, bottom, left, right, unit } and
   * new format { base: {...}, tablet: null, desktop: null }
   */
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      // Already in responsive format (has base key)
      if (val.base !== undefined) {
        return {
          base: val.base || { ...DEFAULT_SPACING },
          tablet: val.tablet || null,
          desktop: val.desktop || null
        };
      }
      // Old format - convert to responsive format
      // Check if it looks like a spacing object (has top, bottom, left, right)
      if (val.top !== undefined || val.bottom !== undefined || val.left !== undefined || val.right !== undefined) {
        return {
          base: {
            top: val.top ?? 0,
            right: val.right ?? 0,
            bottom: val.bottom ?? 0,
            left: val.left ?? 0,
            unit: val.unit || 'px'
          },
          tablet: null,
          desktop: null
        };
      }
    }
    // Return default
    return field.default || {
      base: { ...DEFAULT_SPACING },
      tablet: null,
      desktop: null
    };
  };

  const responsiveObj = normalizeValue(value);

  // Get current spacing for active breakpoint (with fallback to base)
  const currentSpacing = responsiveObj[activeBreakpoint] || responsiveObj.base || { ...DEFAULT_SPACING };

  // Whether current breakpoint is inheriting from a lower breakpoint
  const isInheriting = activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint];

  /**
   * Update a specific side of the spacing
   */
  const updateSide = (side, newValue) => {
    let processedValue;
    const trimmedValue = String(newValue).trim().toLowerCase();

    if (isMargin && (side === 'left' || side === 'right') && trimmedValue === 'auto') {
      processedValue = 'auto';
    } else if (trimmedValue === '' || trimmedValue === 'auto') {
      processedValue = isMargin && (side === 'left' || side === 'right') ? 'auto' : 0;
    } else {
      const parsed = parseFloat(newValue);
      processedValue = isNaN(parsed) ? 0 : parsed;
    }

    const newSpacing = { ...currentSpacing, [side]: processedValue };
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newSpacing
    };
    onChange(newResponsiveObj);
  };

  /**
   * Update all sides at once (for compact "All" input)
   */
  const updateAllSides = (newValue) => {
    const trimmedValue = String(newValue).trim().toLowerCase();
    let processedValue;

    if (trimmedValue === '' || trimmedValue === 'auto') {
      processedValue = 0;
    } else {
      const parsed = parseFloat(newValue);
      processedValue = isNaN(parsed) ? 0 : parsed;
    }

    const newSpacing = {
      ...currentSpacing,
      top: processedValue,
      right: processedValue,
      bottom: processedValue,
      left: processedValue
    };
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newSpacing
    };
    onChange(newResponsiveObj);
  };

  /**
   * Update the unit for current spacing
   */
  const updateUnit = (newUnit) => {
    const newSpacing = { ...currentSpacing, unit: newUnit };
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newSpacing
    };
    onChange(newResponsiveObj);
  };

  /**
   * Clear override for non-base breakpoints (inherit from base)
   */
  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null
      };
      onChange(newResponsiveObj);
    }
  };

  /**
   * Check if a side supports 'auto' value
   */
  const supportsAuto = (key) => isMargin && (key === 'left' || key === 'right');

  // Render breakpoint pills (same pattern as ResponsiveDimensionField)
  const renderBreakpointPills = () => (
    <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
      {BREAKPOINTS.map((bp) => {
        const Icon = bp.icon;
        const hasOverride = bp.id !== 'base' && responsiveObj[bp.id];
        return (
          <button
            key={bp.id}
            type="button"
            onClick={() => handleBreakpointChange(bp.id)}
            disabled={disabled}
            title={bp.label}
            className={`relative p-1 rounded transition-colors
              ${activeBreakpoint === bp.id
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
              } ${disabled ? 'opacity-50' : ''}`}
          >
            <Icon className="w-3 h-3" />
            {hasOverride && (
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
            )}
          </button>
        );
      })}
    </div>
  );

  // Compact mode rendering - visual box model editor
  if (compact) {
    const isAutoLR = currentSpacing.left === 'auto' && currentSpacing.right === 'auto';

    /**
     * Handle arrow key increment/decrement on spacing inputs.
     * Up/Down by 1, Shift+Up/Down by 10.
     */
    const handleSpacingKeyDown = (e, side) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const currentVal = currentSpacing[side];
      if (currentVal === 'auto') return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const delta = e.key === 'ArrowUp' ? step : -step;
      const parsed = parseFloat(currentVal);
      const newVal = (isNaN(parsed) ? 0 : parsed) + delta;

      if (isLinked) {
        updateAllSides(newVal);
      } else {
        updateSide(side, newVal);
      }
    };

    /**
     * Handle value change for a side input, respecting the linked state.
     */
    const handleSideChange = (side, rawValue) => {
      const trimmed = String(rawValue).trim().toLowerCase();
      if (isMargin && (side === 'left' || side === 'right') && (trimmed === 'a' || trimmed === 'auto')) {
        updateSide(side, 'auto');
        return;
      }
      if (isLinked) {
        updateAllSides(rawValue);
      } else {
        updateSide(side, rawValue);
      }
    };

    /**
     * Toggle auto for both left and right margins simultaneously.
     */
    const toggleAutoLR = () => {
      if (isAutoLR) {
        const newSpacing = { ...currentSpacing, left: 0, right: 0 };
        const newResponsiveObj = { ...responsiveObj, [activeBreakpoint]: newSpacing };
        onChange(newResponsiveObj);
      } else {
        const newSpacing = { ...currentSpacing, left: 'auto', right: 'auto' };
        const newResponsiveObj = { ...responsiveObj, [activeBreakpoint]: newSpacing };
        onChange(newResponsiveObj);
      }
    };

    /**
     * Render a single spacing input positioned on a side of the box model.
     */
    const renderSideInput = (side) => {
      const sideValue = currentSpacing[side];
      const isAuto = sideValue === 'auto';

      return (
        <input
          type="text"
          inputMode={isAuto ? 'text' : 'numeric'}
          value={isAuto ? 'auto' : (sideValue != null ? sideValue : '')}
          placeholder="0"
          onChange={(e) => handleSideChange(side, e.target.value)}
          onKeyDown={(e) => handleSpacingKeyDown(e, side)}
          disabled={disabled}
          title={isInheriting ? `${side} (inherited from base)` : side}
          className={`w-14 px-1 py-0.5 text-[11px] text-center border rounded
            bg-white border-gray-200
            focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none
            ${isAuto ? 'text-indigo-600 font-medium text-[10px]' : isInheriting ? 'text-gray-400' : 'text-gray-700'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
        />
      );
    };

    const typeLabel = isMargin ? 'margin' : 'padding';

    return (
      <div className="space-y-1.5">
        {/* Top row: label + breakpoint pills */}
        <div className="flex items-center justify-between">
          <label className="text-xs text-gray-500 truncate">
            {field.label}
          </label>
          {renderBreakpointPills()}
        </div>

        {/* Box model visualization */}
        <div className="relative border border-gray-200 rounded-lg p-3 bg-white">
          {/* Top input - centered above the inner box */}
          <div className="flex justify-center mb-1.5">
            {renderSideInput('top')}
          </div>

          {/* Middle row: left input, inner box, right input */}
          <div className="flex items-center justify-between gap-1.5">
            {/* Left input */}
            <div className="flex-shrink-0">
              {renderSideInput('left')}
            </div>

            {/* Inner content box */}
            <div className="flex-1 flex items-center justify-center bg-gray-50 border border-gray-200 rounded py-3 min-h-[36px]">
              <span className="text-gray-400 text-[10px] uppercase tracking-wider select-none">
                {typeLabel}
              </span>
            </div>

            {/* Right input */}
            <div className="flex-shrink-0">
              {renderSideInput('right')}
            </div>
          </div>

          {/* Bottom input - centered below the inner box */}
          <div className="flex justify-center mt-1.5">
            {renderSideInput('bottom')}
          </div>
        </div>

        {/* Controls row: link toggle, unit selector, auto button */}
        <div className="flex items-center gap-1.5">
          {/* Link/Unlink toggle */}
          <button
            type="button"
            onClick={() => setIsLinked(!isLinked)}
            disabled={disabled}
            title={isLinked ? 'Unlink sides (edit independently)' : 'Link sides (edit all at once)'}
            className={`p-1.5 rounded border transition-colors
              ${isLinked
                ? 'bg-blue-50 border-blue-300 text-blue-600'
                : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLinked ? (
              <Link2 className="w-3.5 h-3.5" />
            ) : (
              <Unlink2 className="w-3.5 h-3.5" />
            )}
          </button>

          {/* Unit selector */}
          <select
            value={currentSpacing.unit}
            onChange={(e) => updateUnit(e.target.value)}
            disabled={disabled}
            className={`px-1.5 py-1 text-xs border rounded bg-white border-gray-200
              focus:border-blue-400 focus:ring-1 focus:ring-blue-400
              ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
          >
            {COMPACT_UNITS.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>

          {/* Auto L+R button (margin only) */}
          {isMargin && (
            <button
              type="button"
              onClick={toggleAutoLR}
              disabled={disabled}
              title={isAutoLR ? 'Remove auto from left and right' : 'Set left and right to auto (center)'}
              className={`ml-auto px-2 py-1 text-[11px] font-medium rounded border transition-colors
                ${isAutoLR
                  ? 'bg-indigo-500 text-white border-indigo-600'
                  : 'bg-white text-gray-500 border-gray-200 hover:text-gray-700 hover:border-gray-300'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Auto L+R
            </button>
          )}
        </div>
      </div>
    );
  }

  // Non-compact mode (full expanded view)
  const sides = [
    { key: 'top', label: 'Top' },
    { key: 'right', label: 'Right' },
    { key: 'bottom', label: 'Bottom' },
    { key: 'left', label: 'Left' },
  ];

  return (
    <div className="space-y-3">
      <label className="block text-xs font-medium text-gray-700">
        {field.label}
      </label>

      {/* Breakpoint selector tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded">
        {BREAKPOINTS.map((bp) => (
          <button
            key={bp.id}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              activeBreakpoint === bp.id
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleBreakpointChange(bp.id)}
            disabled={disabled}
          >
            {bp.label}
            {responsiveObj[bp.id] && bp.id !== 'base' && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-300 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Unit selector */}
      <div>
        <label className="block text-xs text-gray-600 mb-1">Unit</label>
        <select
          value={currentSpacing.unit}
          onChange={(e) => updateUnit(e.target.value)}
          disabled={disabled}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
        >
          {UNITS.map((unit) => (
            <option key={unit.value} value={unit.value}>
              {unit.label}
            </option>
          ))}
        </select>
      </div>

      {/* Spacing inputs in 2x2 grid */}
      <div className="grid grid-cols-2 gap-2">
        {sides.map(({ key, label }) => {
          const sideValue = currentSpacing[key];
          const isAuto = sideValue === 'auto';
          const canBeAuto = supportsAuto(key);

          return (
            <div key={key}>
              <label className="block text-xs text-gray-600 mb-1">{label}</label>
              <input
                type={isAuto ? 'text' : 'number'}
                value={isAuto ? 'auto' : sideValue}
                onChange={(e) => updateSide(key, e.target.value)}
                disabled={disabled || isAuto}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-600"
                placeholder="0"
                step="0.1"
              />
              {canBeAuto && (
                <button
                  type="button"
                  onClick={() => updateSide(key, isAuto ? '0' : 'auto')}
                  disabled={disabled}
                  className={`w-full mt-1 px-2 py-1 text-xs rounded border transition-colors ${
                    isAuto
                      ? 'bg-indigo-500 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isAuto ? '✓ Auto' : 'Set Auto'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Preview */}
      <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
        Preview: {currentSpacing.top}{currentSpacing.unit} {currentSpacing.right === 'auto' ? 'auto' : currentSpacing.right + currentSpacing.unit} {currentSpacing.bottom}{currentSpacing.unit} {currentSpacing.left === 'auto' ? 'auto' : currentSpacing.left + currentSpacing.unit}
      </div>

      {/* Clear override button for non-base breakpoints */}
      {activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint] && (
        <button
          onClick={clearOverride}
          disabled={disabled}
          className="w-full text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
        >
          Clear {activeBreakpoint} override (inherit from base)
        </button>
      )}

      {/* Info text for inheritance */}
      {activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint] && (
        <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
          Currently inheriting from Mobile (base)
        </div>
      )}
    </div>
  );
}
